import { create } from "zustand";

import { montageApi, readVideoDuration } from "../api";
import type { UploadItem } from "../components/FileDrop";
import { kindOf } from "../components/FileDrop";
import { notify } from "./notify";
import { captureFrame } from "./poster";
import { onSettle, realItemId, realTaskId } from "./tempIds";
import { uid } from "./utils";

/**
 * Загрузка черновика монтажёра — в фоне, а не в окне ролика (09.10.2026).
 *
 * Раньше загрузка жила в окне: закрыл его — файл оборвался; через 5 секунд
 * после постановки своей публикации окно перерисовывалось под настоящий id —
 * и файл тоже пропадал. Монтажёры загружали по два раза.
 *
 * Теперь загрузки лежат здесь по id ролика и переживают и закрытие окна, и
 * смену id. «Сдать ролик» можно нажать до конца загрузки: ролик сдастся сам,
 * когда файл догрузится.
 */

type Submit = (taskId: string, itemId: string, files: UploadItem[]) => void;

interface Entry {
  taskId: string;
  title: string;
  files: UploadItem[];
  comment: string;
  /** нажали «Сдать» до конца загрузки */
  submit?: Submit;
}

interface BgState {
  entries: Record<string, Entry>;
}

export const useBgUploads = create<BgState>()(() => ({ entries: {} }));

const controllers: Record<string, AbortController> = {};

const setEntry = (itemId: string, fn: (e: Entry) => Entry) =>
  useBgUploads.setState((s) => {
    const e = s.entries[itemId];
    return e ? { entries: { ...s.entries, [itemId]: fn(e) } } : s;
  });

const patchFile = (itemId: string, fileId: string, patch: Partial<UploadItem>) =>
  setEntry(itemId, (e) => ({ ...e, files: e.files.map((f) => (f.id === fileId ? { ...f, ...patch } : f)) }));

/** Ролик получил настоящий id — переносим его загрузки под новый ключ. */
onSettle((tempTask, realTask, items) => {
  useBgUploads.setState((s) => {
    const entries = { ...s.entries };
    for (const { temp, real } of items) {
      if (entries[temp]) {
        entries[real] = { ...entries[temp], taskId: realTask };
        delete entries[temp];
      }
    }
    for (const [k, e] of Object.entries(entries)) if (e.taskId === tempTask) entries[k] = { ...e, taskId: realTask };
    return { entries };
  });
});

function ensure(itemId: string, taskId: string, title: string) {
  useBgUploads.setState((s) => (s.entries[itemId] ? s : { entries: { ...s.entries, [itemId]: { taskId, title, files: [], comment: "" } } }));
}

/** Готово ли всё у ролика — и, если нажали «Сдать», сдать. */
function maybeSubmit(itemId: string) {
  const e = useBgUploads.getState().entries[itemId];
  if (!e?.submit || e.files.length === 0 || !e.files.every((f) => f.done && f.path)) return;
  const submit = e.submit;
  clear(itemId);
  void realTaskId(e.taskId).then((taskId) => submit(taskId, realItemId(itemId), e.files));
}

export function addFiles(itemId: string, taskId: string, title: string, files: File[]) {
  ensure(itemId, taskId, title);
  for (const f of files) {
    const kind = kindOf(f.name, f.type);
    const item: UploadItem = { id: uid(), name: f.name, size: f.size, kind, progress: 0, done: false };
    if (kind === "image" || kind === "video") item.url = URL.createObjectURL(f);
    if (kind === "video") void readVideoDuration(f).then((d) => d && patchFile(key(itemId), item.id, { duration: d }));
    setEntry(itemId, (e) => ({ ...e, files: [...e.files, item] }));

    const ctrl = new AbortController();
    controllers[item.id] = ctrl;
    void (async () => {
      try {
        // id задачи берём в момент загрузки: за это время он мог стать настоящим
        const current = () => useBgUploads.getState().entries[realItemId(itemId)]?.taskId ?? useBgUploads.getState().entries[itemId]?.taskId ?? taskId;
        // Превью готовится параллельно: его сбой или медленная загрузка
        // не должны задерживать сдачу уже загруженного видео.
        let posterPath: string | undefined;
        if (kind === "video") {
          void captureFrame(f).then(async (frame) => {
            if (!frame || ctrl.signal.aborted) return;
            posterPath = await montageApi.upload("poster", frame, { taskId: current(), fileName: "poster.jpg", signal: ctrl.signal });
            patchFile(key(itemId), item.id, { posterPath });
          }).catch(() => undefined);
        }
        const path = await montageApi.upload("version", f, {
          taskId: current(),
          signal: ctrl.signal,
          onProgress: (p) => patchFile(key(itemId), item.id, { progress: Math.min(0.99, p / 100) }),
        });
        patchFile(key(itemId), item.id, { progress: 1, done: true, path, posterPath });
        maybeSubmit(key(itemId));
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        notify(`«${f.name}» не загрузился: ${(error as Error).message}`, { icon: "rework" });
        setEntry(key(itemId), (e) => ({ ...e, files: e.files.filter((x) => x.id !== item.id), submit: undefined }));
      } finally {
        delete controllers[item.id];
      }
    })();
  }
}

/** Ключ мог переехать с временного id ролика на настоящий. */
const key = (itemId: string) => (useBgUploads.getState().entries[itemId] ? itemId : realItemId(itemId));

export function removeFile(itemId: string, fileId: string) {
  controllers[fileId]?.abort();
  delete controllers[fileId];
  setEntry(key(itemId), (e) => ({ ...e, files: e.files.filter((f) => f.id !== fileId), submit: undefined }));
}

export function setComment(itemId: string, taskId: string, title: string, comment: string) {
  ensure(itemId, taskId, title);
  setEntry(itemId, (e) => ({ ...e, comment }));
}

/** «Сдать» сейчас или, если файл ещё грузится, — как только догрузится. */
export function submitWhenReady(itemId: string, submit: Submit) {
  setEntry(key(itemId), (e) => ({ ...e, submit }));
  maybeSubmit(key(itemId));
}

export function clear(itemId: string) {
  const entry = useBgUploads.getState().entries[itemId];
  for (const file of entry?.files ?? []) {
    controllers[file.id]?.abort();
    delete controllers[file.id];
    if (file.url?.startsWith("blob:")) URL.revokeObjectURL(file.url);
  }
  useBgUploads.setState((s) => {
    const entries = { ...s.entries };
    delete entries[itemId];
    return { entries };
  });
}

export const activeUploads = (entries: Record<string, Entry>) =>
  Object.entries(entries).filter(([, e]) => e.files.some((f) => !f.done));

/** Закрыть вкладку посреди загрузки — файл не доедет: браузер спросит. */
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (activeUploads(useBgUploads.getState().entries).length) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}
